import React, { useState, lazy, memo } from 'react';
import { ArrowRightIcon, ArrowUpRightIcon, BedDoubleIcon, CalendarDaysIcon, ChevronDownIcon, CirclePlayIcon, Clock3Icon, CoffeeIcon, CompassIcon, HeartHandshakeIcon, MapPinIcon, MountainIcon, SoupIcon, UtensilsCrossedIcon } from 'lucide-react';
import { Footer } from '../components/Footer';
import { Header } from '../components/Header';
type ItineraryDay = {
  day: number;
  title: string;
  route: string;
  elevation: string;
  walking: string;
  distance: string;
  image: string;
  description: string;
  note?: string;
};
const facts = [{
  icon: CalendarDaysIcon,
  label: 'Duration',
  value: '14–17 days'
}, {
  icon: MountainIcon,
  label: 'Highest point',
  value: '5,106m'
}, {
  icon: CompassIcon,
  label: 'Grade',
  value: 'Challenging'
}, {
  icon: MapPinIcon,
  label: 'Start / finish',
  value: 'Kathmandu'
}];
const days: ItineraryDay[] = [{
  day: 1,
  title: 'Arrival in Kathmandu',
  route: 'Tribhuvan International Airport · Thamel',
  elevation: '1,400m',
  walking: 'Easy arrival day',
  distance: 'Airport transfer',
  image: 'https://images.unsplash.com/photo-1544735716-392fe2489ffa?auto=format&fit=crop&w=1100&q=85',
  description: 'Meet your trek leader in Kathmandu and settle into the rhythm of Nepal. Over a welcome dinner, we look ahead to the mountains and check your final gear.'
}, {
  day: 2,
  title: 'Drive to Machha Khola',
  route: 'Kathmandu · Arughat · Machha Khola',
  elevation: '900m',
  walking: '7–8 hrs drive',
  distance: '160 km',
  image: 'https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?auto=format&fit=crop&w=1100&q=85',
  description: 'Follow the Trishuli and Budhi Gandaki rivers west into the foothills. The road narrows, terraces rise, and the first true sense of the Manaslu region begins.',
  note: 'Road conditions vary with the season. We keep this day unhurried and build in time for the journey itself.'
}, {
  day: 3,
  title: 'Walk to Jagat',
  route: 'Machha Khola · Khorlabesi · Jagat',
  elevation: '1,340m',
  walking: '6–7 hrs',
  distance: '22 km',
  image: 'https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?auto=format&fit=crop&w=1100&q=85',
  description: 'A first full day on the trail: suspension bridges, steep-sided valley walls, and a gradual climb toward the stone village of Jagat.'
}, {
  day: 4,
  title: 'Jagat to Deng',
  route: 'Jagat · Philim · Bihi Phedi · Deng',
  elevation: '1,860m',
  walking: '6–7 hrs',
  distance: '20 km',
  image: 'https://images.unsplash.com/photo-1518002054494-3a6f94352e9d?auto=format&fit=crop&w=1100&q=85',
  description: 'Enter the restricted Manaslu Conservation Area. The trail passes through forest and Gurung villages, with the valley beginning to open toward higher country.'
}, {
  day: 5,
  title: 'Deng to Namrung',
  route: 'Deng · Ghap · Namrung',
  elevation: '2,630m',
  walking: '6–7 hrs',
  distance: '19 km',
  image: 'https://images.unsplash.com/photo-1533130061792-64b345e4a833?auto=format&fit=crop&w=1100&q=85',
  description: 'Climb through mossy forest and small settlements into Nubri country. In Namrung, the first wide views toward the snow peaks arrive.'
}, {
  day: 6,
  title: 'Namrung to Samagaun',
  route: 'Namrung · Lho · Shyala · Samagaun',
  elevation: '3,530m',
  walking: '6–7 hrs',
  distance: '18 km',
  image: 'https://images.unsplash.com/photo-1486911278844-a81c5267e227?auto=format&fit=crop&w=1100&q=85',
  description: 'Walk beneath Manaslu’s immense north face, through the monastery village of Lho and across the broad yak pastures of Shyala.',
  note: 'This is one of the journey’s most photogenic days. Keep your camera accessible, but leave time to simply look.'
}];
const faqs = [{
  question: 'How fit do I need to be for the Manaslu Circuit?',
  answer: 'You should be comfortable walking for six to seven hours on consecutive days with sustained uphill and downhill terrain. A regular program of hiking, cardio, and leg strength work in the months before departure is ideal.'
}, {
  question: 'When is the best time to trek Manaslu?',
  answer: 'Spring (March to May) and autumn (October to November) bring the most reliable weather and clear mountain views. The route is quieter than Everest or Annapurna, even in the main seasons.'
}, {
  question: 'Are permits included in the trip price?',
  answer: 'Yes. Your restricted-area permit, Manaslu Conservation Area Permit, Annapurna Conservation Area Permit, and required local paperwork are included in the listed price.'
}, {
  question: 'What kind of accommodation should I expect?',
  answer: 'We stay in carefully selected local teahouses. Rooms are simple and shared bathrooms are common at higher elevations, but the welcome is warm and meals are freshly prepared.'
}];
function MealRow() {
  return <div className="flex flex-wrap gap-x-4 gap-y-2 text-[10px] font-bold uppercase tracking-[0.12em] text-[#5c6c61]" data-id="element-629">
      <span className="flex items-center gap-1.5" data-id="element-630">
        <CoffeeIcon className="h-3.5 w-3.5 text-[#a66e2e]" data-id="element-631" /> Breakfast
      </span>
      <span className="flex items-center gap-1.5" data-id="element-632">
        <SoupIcon className="h-3.5 w-3.5 text-[#a66e2e]" data-id="element-633" /> Lunch
      </span>
      <span className="flex items-center gap-1.5" data-id="element-634">
        <UtensilsCrossedIcon className="h-3.5 w-3.5 text-[#a66e2e]" data-id="element-635" /> Dinner
      </span>
    </div>;
}
export function ManasluCircuitTrek() {
  const [openDay, setOpenDay] = useState<number>(1);
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const [showFullOverview, setShowFullOverview] = useState(false);
  return <div className="min-h-screen overflow-hidden bg-[#f8f5ed] text-[#17362e]" data-id="element-636">
      <Header data-id="element-637" />
      <main data-id="element-638">
        <section className="relative flex min-h-[680px] items-end bg-[#17362e] pb-16 pt-32 text-white sm:min-h-[760px] sm:pb-20" data-id="element-639">
          <img src="https://images.unsplash.com/photo-1486911278844-a81c5267e227?auto=format&fit=crop&w=2200&q=90" alt="Snowy Himalayan ridges on the Manaslu Circuit" className="absolute inset-0 h-full w-full object-cover opacity-80" data-id="element-640" />
          <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(8,27,22,0.77),rgba(8,27,22,0.16)_72%),linear-gradient(0deg,rgba(8,27,22,0.65),transparent_60%)]" data-id="element-641" />
          <div className="relative mx-auto w-full max-w-[1440px] px-5 sm:px-8 lg:px-12" data-id="element-642">
            <a href="/#treks" className="text-[10px] font-bold uppercase tracking-[0.19em] text-[#efc778]" data-id="element-643">
              ← All treks
            </a>
            <p className="mt-10 text-[10px] font-bold uppercase tracking-[0.28em] text-[#efc778]" data-id="element-644">
              Nepal’s great Himalayan crossing
            </p>
            <h1 className="mt-5 max-w-4xl font-serif text-5xl leading-[0.98] sm:text-7xl lg:text-[82px]" data-id="element-645">
              Manaslu
              <br data-id="element-646" />
              <i className="font-normal" data-id="element-647">Circuit Trek</i>
            </h1>
            <p className="mt-7 max-w-xl text-base leading-7 text-white/85" data-id="element-648">
              A quieter, wilder route around the world’s eighth-highest mountain
              — shaped by prayer walls, glacial valleys, and the high crossing
              of Larkya La.
            </p>
            <a href="#book" className="mt-8 inline-flex items-center gap-2 bg-[#b7d6a8] px-6 py-4 text-[10px] font-bold uppercase tracking-[0.16em] text-[#17362e] transition hover:bg-[#d8ebcd]" data-id="element-649">
              Start planning <ArrowRightIcon className="h-4 w-4" data-id="element-650" />
            </a>
          </div>
        </section>

        <section className="relative z-10 -mt-1 bg-[#f8f5ed]" data-id="element-651">
          <div className="mx-auto grid max-w-[1180px] grid-cols-2 divide-x divide-y divide-[#d5d8cc] border-x border-[#d5d8cc] sm:grid-cols-4" data-id="element-652">
            {facts.map(fact => {
            const Icon = fact.icon;
            return <div key={fact.label} className="p-5 sm:p-7" data-id="element-653">
                  <Icon className="h-5 w-5 text-[#a66e2e]" strokeWidth={1.5} data-id="element-654" />
                  <p className="mt-4 text-[9px] font-bold uppercase tracking-[0.17em] text-[#708075]" data-id="element-655">
                    {fact.label}
                  </p>
                  <p className="mt-1 font-serif text-lg" data-id="element-656">{fact.value}</p>
                </div>;
          })}
          </div>
        </section>

        <section className="py-20 sm:py-28" data-id="element-657">
          <div className="mx-auto grid max-w-[1180px] gap-12 px-5 sm:px-8 lg:grid-cols-[0.72fr_1.28fr] lg:gap-24" data-id="element-658">
            <div data-id="element-659">
              <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#a66e2e]" data-id="element-660">
                The journey
              </p>
              <div className="mt-5 h-px w-16 bg-[#a66e2e]" data-id="element-661" />
            </div>
            <div data-id="element-662">
              <h2 className="font-serif text-4xl leading-[1.12] sm:text-5xl" data-id="element-663">
                Walk beyond the familiar.
              </h2>
              <div className="mt-7 max-w-2xl space-y-5 text-lg leading-8 text-[#52635a]" data-id="element-664">
                <p data-id="element-665">
                  The Manaslu Circuit brings together all the reasons people
                  come to Nepal: a living Buddhist culture, extraordinary high
                  country, and a route that still feels genuinely remote.
                  Beginning in the warm Budhi Gandaki valley, the trail follows
                  an ancient river corridor through terraced fields, hanging
                  forests, and villages shaped by Gurung and Nubri traditions.
                  Each day feels like a gradual change of world: stone houses
                  give way to yak pastures, and the first far-off summits become
                  close enough to fill the sky.
                </p>
                <p data-id="element-666">
                  This is not a trek designed to be rushed. Our route includes
                  time to acclimatise in Samagaun, where Manaslu’s north face
                  rises above monasteries and prayer flags. Your local leader
                  sets a steady pace, helps you understand the landscape, and
                  makes sure the simple pleasures of the trail—a warm tea, a
                  quiet lookout, an unhurried lunch—have room to happen.
                </p>
                {showFullOverview && <>
                    <p data-id="element-667">
                      As the valley climbs, the rhythm becomes beautifully
                      elemental. You walk, rest, eat, and sleep in small
                      family-run teahouses. Mornings begin with a bowl of
                      porridge or Tibetan bread; afternoons often end beside a
                      stove with the sound of the river beyond the window. The
                      route carries you through places where the mountain
                      remains part of daily life, not merely a view. Respectful
                      travel matters here, and our small groups give the
                      communities we visit the space and attention they deserve.
                    </p>
                    <p data-id="element-668">
                      The high point is Larkya La at 5,106 metres, a broad,
                      dramatic pass reached in the blue light before dawn. It is
                      a demanding day, but it is carefully supported: a packed
                      lunch, warm drinks, experienced guides, and a pace that
                      prioritises wellbeing over speed. From the crest, the
                      horizon opens toward Himlung, Annapurna II, and the long
                      ridges of the Tibetan border. The descent to Bimthang is
                      equally memorable, dropping through glacial moraine into a
                      valley of immense walls and soft alpine meadows.
                    </p>
                    <p data-id="element-669">
                      You do not need mountaineering skills for Manaslu, but you
                      do need curiosity, resilience, and a willingness to slow
                      down. The trail asks for good preparation and rewards it
                      with one of Nepal’s most complete high-mountain journeys.
                      By the time you return to Kathmandu, the memories are
                      rarely only of the pass: they are of a guide pointing out
                      a monastery, a child’s wave from a doorway, and the rare
                      feeling of walking somewhere that still moves to its own
                      ancient rhythm.
                    </p>
                  </>}
              </div>
              <button type="button" onClick={() => setShowFullOverview(!showFullOverview)} aria-expanded={showFullOverview} className="mt-7 inline-flex items-center gap-2 border-b border-[#6e9b70] pb-2 text-[10px] font-bold uppercase tracking-[0.16em] text-[#315c3b] transition hover:text-[#17362e]" data-id="element-670">
                {showFullOverview ? 'Show less' : 'Show more'}{' '}
                <ArrowRightIcon className={`h-4 w-4 transition-transform ${showFullOverview ? '-rotate-90' : 'rotate-90'}`} data-id="element-671" />
              </button>
              <div className="mt-8 flex flex-wrap gap-3 text-[10px] font-bold uppercase tracking-[0.14em] text-[#52635a]" data-id="element-672">
                <span className="border border-[#ccd0c3] px-3 py-2" data-id="element-673">
                  Small groups · Max 12
                </span>
                <span className="border border-[#ccd0c3] px-3 py-2" data-id="element-674">
                  Local mountain leaders
                </span>
                <span className="border border-[#ccd0c3] px-3 py-2" data-id="element-675">
                  Teahouse trekking
                </span>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-[#132b27] px-5 py-12 text-[#f8f5ed] sm:px-8 sm:py-16 lg:px-12 lg:py-20" data-id="element-676">
          <div className="mx-auto max-w-[1320px] border border-[#49635d]" data-id="element-677">
            <div className="grid lg:grid-cols-[1fr_0.94fr]" data-id="element-678">
              <div className="bg-[#17362e] px-6 py-10 sm:px-10 sm:py-14 lg:px-14" data-id="element-679">
                <p className="text-[10px] font-bold uppercase tracking-[0.29em] text-[#efc778]" data-id="element-680">
                  Outline itinerary
                </p>
                <h2 className="mt-5 font-serif text-4xl leading-[1.1] text-[#fffaf0] sm:text-5xl" data-id="element-681">
                  The Manaslu traverse
                </h2>

                <ol className="mt-11 border-l border-[#7f968d] pl-5 sm:mt-12 sm:pl-7" data-id="element-682">
                  {[['Day 01', 'Arrival in Kathmandu (1,400 m)', 'Airport transfer and hotel check-in'], ['Day 02', 'Drive from Kathmandu to Macha Khola (930 m)', '8–9 hours'], ['Day 03', 'Trek from Macha Khola to Jagat (1,340 m)', '6–7 hours'], ['Day 04', 'Trek from Jagat to Deng (1,860 m)', '6–7 hours'], ['Day 05', 'Trek from Deng to Namrung (2,630 m)', '6–7 hours'], ['Day 06', 'Trek from Namrung to Samagaon (3,530 m)', '6–7 hours'], ['Day 07', 'Acclimatization Day at Samagaon', 'Optional hike to Birendra Lake or Manaslu Base Camp'], ['Day 08', 'Trek from Samagaon to Samdo (3,875 m)', '3–4 hours'], ['Day 09', 'Trek from Samdo to Dharamsala (Larke Phedi) (4,460 m)', '4–5 hours'], ['Day 10', 'Cross Larke Pass (5,106 m) and trek to Bimthang (3,720 m)', '8–9 hours'], ['Day 11', 'Trek from Bimthang to Dharapani (1,963 m)', '6–7 hours'], ['Day 12', 'Drive from Dharapani to Kathmandu', '9–10 hours'], ['Day 13', 'Free day in Kathmandu', 'Optional sightseeing or leisure'], ['Day 14', 'Final departure from Kathmandu', '']].map(([dayLabel, title, details]) => <li key={dayLabel} className="py-2.5 text-sm leading-6 text-[#fffaf0] sm:py-3 sm:text-base" data-id="element-683">
                      <span className="font-medium" data-id="element-684">{dayLabel}</span>
                      <span className="mx-1.5 text-[#d8ebcd]" aria-hidden="true" data-id="element-685">
                        —
                      </span>
                      <span data-id="element-686">{title}</span>
                      {details && <>
                          <span className="mx-1.5 text-[#d8ebcd]" aria-hidden="true" data-id="element-687">
                            ·
                          </span>
                          <span className="text-[#d8ebcd]" data-id="element-688">{details}</span>
                        </>}
                    </li>)}
                </ol>
              </div>

              <aside aria-labelledby="route-map-heading" className="relative flex min-h-[500px] flex-col overflow-hidden bg-[#e8e5d9] px-6 py-10 text-[#17362e] sm:min-h-[590px] sm:px-10 sm:py-14 lg:min-h-full lg:px-12" data-id="element-689">
                <div className="relative z-10" data-id="element-690">
                  <p className="text-[10px] font-bold uppercase tracking-[0.29em] text-[#17362e]" data-id="element-691">
                    Route map
                  </p>
                  <h3 id="route-map-heading" className="mt-5 max-w-md font-serif text-3xl leading-tight sm:text-4xl" data-id="element-692">
                    From Kathmandu to the high crossing at Larkya La.
                  </h3>
                  <p className="mt-5 max-w-sm text-sm leading-6 text-[#365249] sm:text-base sm:leading-7" data-id="element-693">
                    A steady progression through the Budhi Gandaki valley, Nubri
                    villages, alpine pastures and the circuit’s high pass at
                    5,106 metres.
                  </p>
                </div>

                <figure className="relative z-10 mt-8 overflow-hidden border border-[#577168]/35 bg-[#dce8f0]" data-id="element-694">
                  <img src="https://cdn.magicpatterns.com/uploads/iTP5EDBixMwS8ooq76wTCC/image.png" alt="Manaslu Circuit route map showing the trail from Kathmandu through Machha Khola, Samagaun, Larkya La Pass, Bimthang and Dharapani, with an elevation profile" className="h-auto w-full object-contain" loading="lazy" data-id="element-695" />
                </figure>

                <div className="relative z-10 mt-auto pt-6" data-id="element-696">
                  <div className="border-t border-[#577168]/45 pt-5 text-[10px] font-bold uppercase tracking-[0.16em] text-[#28443b]" data-id="element-697">
                    Kathmandu return · 14 days · 5,106m high point
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </section>

        <section id="itinerary" className="py-20 sm:py-28" data-id="element-698">
          <div className="mx-auto max-w-[1180px] px-5 sm:px-8" data-id="element-699">
            <div className="max-w-2xl" data-id="element-700">
              <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#a66e2e]" data-id="element-701">
                Day by day
              </p>
              <h2 className="mt-4 font-serif text-4xl sm:text-5xl" data-id="element-702">
                Follow the valley upward.
              </h2>
              <p className="mt-5 leading-7 text-[#52635a]" data-id="element-703">
                Select a day to see the route, daily rhythm, meals, and the
                details that matter on the trail.
              </p>
            </div>
            <div className="mt-12 divide-y divide-[#cfd3c8] border-y border-[#cfd3c8]" data-id="element-704">
              {days.map(day => {
              const isOpen = openDay === day.day;
              return <article key={day.day} data-id="element-705">
                    <button type="button" onClick={() => setOpenDay(isOpen ? 0 : day.day)} aria-expanded={isOpen} aria-controls={`day-${day.day}`} className="flex w-full items-center justify-between gap-4 py-6 text-left" data-id="element-706">
                      <span className="flex items-center gap-5 sm:gap-8" data-id="element-707">
                        <span className="text-[10px] font-bold tracking-[0.18em] text-[#a66e2e]" data-id="element-708">
                          DAY {String(day.day).padStart(2, '0')}
                        </span>
                        <span data-id="element-709">
                          <span className="block font-serif text-3xl leading-tight" data-id="element-710">
                            {day.title}
                          </span>
                          <span className="mt-2 block text-sm text-[#66766c]" data-id="element-711">
                            {day.route}
                          </span>
                        </span>
                      </span>
                      <ChevronDownIcon className={`h-5 w-5 shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} data-id="element-712" />
                    </button>
                    {isOpen && <div id={`day-${day.day}`} className="grid gap-6 pb-8 lg:grid-cols-[260px_1fr]" data-id="element-713">
                        <img src={day.image} alt={`${day.title} on the Manaslu route`} className="h-52 w-full object-cover lg:h-full" data-id="element-714" />
                        <div data-id="element-715">
                          <div className="grid gap-3 sm:grid-cols-3" data-id="element-716">
                            <div data-id="element-717">
                              <p className="text-[9px] font-bold uppercase tracking-[0.15em] text-[#718074]" data-id="element-718">
                                Elevation
                              </p>
                              <p className="mt-1 text-sm font-medium" data-id="element-719">
                                {day.elevation}
                              </p>
                            </div>
                            <div data-id="element-720">
                              <p className="text-[9px] font-bold uppercase tracking-[0.15em] text-[#718074]" data-id="element-721">
                                Walking time
                              </p>
                              <p className="mt-1 text-sm font-medium" data-id="element-722">
                                {day.walking}
                              </p>
                            </div>
                            <div data-id="element-723">
                              <p className="text-[9px] font-bold uppercase tracking-[0.15em] text-[#718074]" data-id="element-724">
                                Distance
                              </p>
                              <p className="mt-1 text-sm font-medium" data-id="element-725">
                                {day.distance}
                              </p>
                            </div>
                          </div>
                          <p className="mt-6 max-w-2xl text-base leading-8 text-[#40564a]" data-id="element-726">
                            {day.description}
                          </p>
                          {day.note && <aside className="mt-5 border-l-2 border-[#c97b37] bg-[#f2eadb] px-4 py-3 text-sm leading-6 text-[#5c4a32]" data-id="element-727">
                              <strong className="font-semibold" data-id="element-728">
                                Trail note:{' '}
                              </strong>
                              {day.note}
                            </aside>}
                          <div className="mt-6 border-t border-[#d6d9ce] pt-4" data-id="element-729">
                            <MealRow data-id="element-730" />
                          </div>
                        </div>
                      </div>}
                  </article>;
            })}
            </div>
          </div>
        </section>

        <section id="book" className="bg-[#17362e] py-20 text-white sm:py-28" data-id="element-731">
          <div className="mx-auto grid max-w-[1180px] gap-10 px-5 sm:px-8 lg:grid-cols-[1.05fr_0.95fr]" data-id="element-732">
            <div data-id="element-733">
              <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#efc778]" data-id="element-734">
                Investment
              </p>
              <h2 className="mt-4 font-serif text-4xl sm:text-5xl" data-id="element-735">
                A considered journey,
                <br data-id="element-736" />
                well supported.
              </h2>
              <p className="mt-5 max-w-lg leading-7 text-white/70" data-id="element-737">
                From the first Kathmandu briefing to the final mountain
                breakfast, your trek is designed and led by a local team with
                decades of experience.
              </p>
            </div>
            <div className="bg-[#f8f5ed] p-7 text-[#17362e] sm:p-9" data-id="element-738">
              <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#a66e2e]" data-id="element-739">
                Manaslu Circuit · 14 days
              </p>
              <div className="mt-3 flex items-end gap-2" data-id="element-740">
                <span className="font-serif text-5xl" data-id="element-741">USD 1,890</span>
                <span className="pb-1 text-sm text-[#66766c]" data-id="element-742">per person</span>
              </div>
              <p className="mt-3 text-sm text-[#66766c]" data-id="element-743">
                Based on two travellers. Single room supplement available in
                Kathmandu.
              </p>
              <a href="mailto:hello@manaslutrekking.com?subject=Manaslu%20Circuit%20Trek%20Inquiry" className="mt-7 flex items-center justify-center gap-2 bg-[#d8a75a] px-5 py-4 text-[10px] font-bold uppercase tracking-[0.16em] transition hover:bg-[#efc778]" data-id="element-744">
                Request departure dates <ArrowUpRightIcon className="h-4 w-4" data-id="element-745" />
              </a>
            </div>
          </div>
        </section>

        <section className="py-20 sm:py-28" data-id="element-746">
          <div className="mx-auto grid max-w-[1180px] gap-10 px-5 sm:px-8 lg:grid-cols-2" data-id="element-747">
            <div data-id="element-748">
              <p className="text-[10px] font-bold uppercase tracking-[0.23em] text-[#a66e2e]" data-id="element-749">
                Included
              </p>
              <h2 className="mt-4 font-serif text-3xl" data-id="element-750">
                What your trip covers.
              </h2>
              <ul className="mt-7 space-y-3 text-sm leading-6 text-[#52635a]" data-id="element-751">
                {['Airport transfers and two nights in Kathmandu', 'Licensed local guide, assistant guide, and porters', 'All trek permits and conservation-area fees', 'Teahouse accommodation during the trek', 'Breakfast, lunch, and dinner on trekking days', 'Ground transport between Kathmandu and the trailhead'].map(item => <li key={item} className="flex gap-3" data-id="element-752">
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#a66e2e]" data-id="element-753" />
                    {item}
                  </li>)}
              </ul>
            </div>
            <div data-id="element-754">
              <p className="text-[10px] font-bold uppercase tracking-[0.23em] text-[#a66e2e]" data-id="element-755">
                Not included
              </p>
              <h2 className="mt-4 font-serif text-3xl" data-id="element-756">
                A few things to arrange.
              </h2>
              <ul className="mt-7 space-y-3 text-sm leading-6 text-[#52635a]" data-id="element-757">
                {['International flights and Nepal entry visa', 'Travel and rescue insurance', 'Lunch and dinner in Kathmandu', 'Personal trekking gear and drinks', 'Tips for your mountain crew', 'Extra accommodation caused by weather or delays'].map(item => <li key={item} className="flex gap-3" data-id="element-758">
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#a66e2e]" data-id="element-759" />
                    {item}
                  </li>)}
              </ul>
            </div>
          </div>
          <div className="mx-auto mt-12 max-w-[1180px] px-5 sm:px-8" data-id="element-760">
            <aside className="border border-[#a9cba7] bg-[#e1efdc] p-7 sm:p-9" data-id="element-761">
              <div className="flex flex-col justify-between gap-5 md:flex-row md:items-start" data-id="element-762">
                <div className="flex gap-4" data-id="element-763">
                  <HeartHandshakeIcon className="h-9 w-9 shrink-0 text-[#315c3b]" strokeWidth={1.35} data-id="element-764" />
                  <div data-id="element-765">
                    <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#46734f]" data-id="element-766">
                      Optional add-ons
                    </p>
                    <h3 className="mt-2 font-serif text-3xl" data-id="element-767">
                      Shape the journey around you.
                    </h3>
                    <p className="mt-2 max-w-2xl text-sm leading-6 text-[#426149]" data-id="element-768">
                      Add time in Kathmandu or make your trail transfers more
                      comfortable with a private jeep.
                    </p>
                  </div>
                </div>
                <a href="mailto:hello@manaslutrekking.com?subject=Manaslu%20Circuit%20Add-ons" className="shrink-0 text-[10px] font-bold uppercase tracking-[0.15em] text-[#315c3b]" data-id="element-769">
                  Ask about add-ons{' '}
                  <ArrowUpRightIcon className="ml-1 inline h-4 w-4" data-id="element-770" />
                </a>
              </div>
              <div className="mt-7 grid gap-px bg-[#a9cba7] sm:grid-cols-3" data-id="element-771">
                {[['Kathmandu Day Tour', 'US$ 90'], ['Kathmandu to Machkhola Jeep', 'US$ 300'], ['Dharapni to Kathmandu Jeep', 'US$ 400']].map(([name, price]) => <div key={name} className="bg-[#f4faef] p-5" data-id="element-772">
                    <p className="text-sm font-medium text-[#254c30]" data-id="element-773">{name}</p>
                    <p className="mt-3 font-serif text-2xl text-[#17362e]" data-id="element-774">
                      {price}
                    </p>
                  </div>)}
              </div>
            </aside>
          </div>
        </section>

        <section className="bg-[#e7e7db] py-20 sm:py-28" data-id="element-775">
          <div className="mx-auto max-w-[1180px] px-5 sm:px-8" data-id="element-776">
            <div className="flex flex-col justify-between gap-5 sm:flex-row sm:items-end" data-id="element-777">
              <div data-id="element-778">
                <p className="text-[10px] font-bold uppercase tracking-[0.23em] text-[#a66e2e]" data-id="element-779">
                  From the trail
                </p>
                <h2 className="mt-4 font-serif text-4xl" data-id="element-780">
                  A glimpse of Manaslu.
                </h2>
              </div>
              <a href="/#videos" className="text-[10px] font-bold uppercase tracking-[0.16em]" data-id="element-781">
                More field stories{' '}
                <ArrowRightIcon className="ml-1 inline h-4 w-4" data-id="element-782" />
              </a>
            </div>
            <div className="mt-10 grid gap-4 md:grid-cols-3" data-id="element-783">
              <img src="https://images.unsplash.com/photo-1548013146-72479768bada?auto=format&fit=crop&w=900&q=85" alt="Mountain valley along the Manaslu trail" className="h-72 w-full object-cover md:col-span-2" data-id="element-784" />
              <img src="https://images.unsplash.com/photo-1500534314209-a25ddb2bd429?auto=format&fit=crop&w=700&q=85" alt="Trekker on a Himalayan journey" className="h-72 w-full object-cover" data-id="element-785" />
            </div>
            <a href="/#videos" className="group relative mt-4 block overflow-hidden" data-id="element-786">
              <img src="https://images.unsplash.com/photo-1528181304800-259b08848526?auto=format&fit=crop&w=1600&q=85" alt="Himalayan mountain pass featured in a Manaslu field film" className="h-64 w-full object-cover transition duration-700 group-hover:scale-105 sm:h-96" data-id="element-787" />
              <span className="absolute inset-0 flex items-center justify-center bg-[#10261f]/25" data-id="element-788">
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-[#c92727] text-white" data-id="element-789">
                  <CirclePlayIcon className="ml-0.5 h-8 w-8 fill-white" data-id="element-790" />
                </span>
              </span>
            </a>
          </div>
        </section>

        <section className="py-20 sm:py-28" data-id="element-791">
          <div className="mx-auto grid max-w-[1180px] gap-10 px-5 sm:px-8 lg:grid-cols-[0.7fr_1.3fr]" data-id="element-792">
            <div data-id="element-793">
              <p className="text-[10px] font-bold uppercase tracking-[0.23em] text-[#a66e2e]" data-id="element-794">
                Questions, answered
              </p>
              <h2 className="mt-4 font-serif text-4xl" data-id="element-795">Before you go.</h2>
            </div>
            <div className="divide-y divide-[#cfd3c8] border-y border-[#cfd3c8]" data-id="element-796">
              {faqs.map((faq, index) => {
              const open = openFaq === index;
              return <div key={faq.question} data-id="element-797">
                    <button type="button" onClick={() => setOpenFaq(open ? null : index)} aria-expanded={open} className="flex w-full items-center justify-between gap-5 py-6 text-left font-serif text-xl" data-id="element-798">
                      <span data-id="element-799">{faq.question}</span>
                      <ChevronDownIcon className={`h-5 w-5 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} data-id="element-800" />
                    </button>
                    {open && <p className="max-w-2xl pb-6 text-sm leading-7 text-[#52635a]" data-id="element-801">
                        {faq.answer}
                      </p>}
                  </div>;
            })}
            </div>
          </div>
        </section>

        <section className="bg-[#c8dfbd] px-5 py-16 text-[#17362e] sm:px-8 lg:px-12" data-id="element-802">
          <div className="mx-auto flex max-w-[1100px] flex-col items-start justify-between gap-7 md:flex-row md:items-center" data-id="element-803">
            <div data-id="element-804">
              <p className="text-[10px] font-bold uppercase tracking-[0.2em]" data-id="element-805">
                Your departure begins here
              </p>
              <h2 className="mt-3 font-serif text-3xl sm:text-4xl" data-id="element-806">
                Ready to walk the Manaslu Circuit?
              </h2>
            </div>
            <a href="mailto:hello@manaslutrekking.com?subject=Manaslu%20Circuit%20Trek%20Inquiry" className="inline-flex items-center gap-2 bg-[#17362e] px-6 py-4 text-[10px] font-bold uppercase tracking-[0.16em] text-white transition hover:bg-[#10261f]" data-id="element-807">
              Make an inquiry <ArrowRightIcon className="h-4 w-4" data-id="element-808" />
            </a>
          </div>
        </section>
      </main>
      <Footer data-id="element-809" />
    </div>;
}